import { PageModule } from '../../utils/page.module';
import { Icon } from '../../general/icons/Icon';
import {
  convertToReducedProduct,
  getProductDetail,
  HamiltonDeviceConfiguration,
  HamiltonDocument,
  HamiltonProduct,
  HamiltonProductDetail,
  packagingUnitOrDefault
} from '../../api/products-api';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import {
  createDivElementFromString,
  createHtmlElementFromString,
  createPatientGroupIconsFromString,
  createUseIconsFromId,
  getFallbackIconForCategory,
  formatText
} from '../../utils/html.utils';
import { getPriceWithCurrencyForOnePiece } from '../../api/shopping-cart-api';
import { getUrlForImage } from '../../utils/image.utils';
import { baseUrl } from '../../api/general-api';
import { addToShoppingCart } from './e-catalog.utils';
import { wishListData } from '../../wish-list-data.module';
import { textAndImageComponent } from '../text-and-image-component/text-and-image-component.module';
import { header } from '../header/header.module';
import { eCatalog } from './e-catalog.module';
import { convertFileSize } from '../../utils/helper.utils';

/**
 * Creates the detail view for the e-catalog.
 */
export class ECatalogDetail extends PageModule {
  /**
   * Selector that is rendered in this view.
   */
  componentSelector = '.section--product-detail';

  /**
   * Markup for the overview
   */
  template = `<section class="section section--product-detail">
  <div class="section__element section__element--back-button" data-animate-when-visible="fade-in-up">
    <a role="button" class="button" id="e-catalog-detail-back-button">
      <span class="button__icon-left">${Icon.leftArrow}</span>
      <span class="button__text"></span>
    </a>
  </div>
  <div class="section__element" data-animate-when-visible="fade-in-up">
    <div class="text-and-image-component text-and-image-component--image-small">
      <div class="text-and-image-component__left-content">
        <div class="text-and-image-component__image-wrapper"></div>
        <div class="text-and-image-component__thumbnails"></div>
      </div>
      <div class="text-and-image-component__right-content">
        <div class="product-detail-heading">
          <span class="product-detail__id"></span>
          <h2 class="product-detail__headline"></h2>
          <div class="product-detail__buttons">
            <button class="button button--icon-only" id="product-detail-wishlist-button" data-overlay-id="select-wish-list-overlay" aria-label="Wishlist">
              <span class="button__icon-single">${Icon.pin}</span>
            </button>
            <button class="button button--icon-only" id="product-detail-cart-button" aria-label="Shopping cart">
              <span class="button__icon-single">${Icon.shoppingCart}</span>
            </button>
            <div class="social-share-wrapper">
              <div class="social-share-button">
                <button class="button button--icon-only" aria-label="Share">${Icon.share}</button>
                <div class="social-share social-share--hidden">
                  <div class="social-share__text"></div>
                  <div class="social-share__icons">
                    <a class="social-share__mail social-share__icon">${Icon.mail}</a>
                    <button class="social-share__copy social-share__icon" aria-label="Copy">${Icon.copy}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <table class="product-detail-table"></table>
      </div>
    </div>
  </div>
  <div class="section__element" data-animate-when-visible="fade-in-up">
    <div class="product-detail-description">
      <div class="product-detail__table"></div>
    </div>
  </div>
</section>`;

  /**
   * One row in the product details table.
   */
  productDetailsRow = `
  <tr>
    <th></th>
    <td></td>
  </tr>`;

  /**
   * Markup for the downloads table.
   */
  downloadsTable = `<div class="section__element" data-animate-when-visible="fade-in-up">
    <div class="downloads-table">
      <h3 class="downloads-table__headline"></h3>
    </div>
  </div>`;

  /**
   * Markup for a download item used in the downloads table.
   */
  downloadItem = `<a class="downloads-item no-anchor-styles" download>
    <span class="downloads-item__icon"></span>
    <div class="downloads-item__titles">
        <span class="downloads-item__type"></span>
        <h4 class="downloads-item__title"></h4>
    </div>
    <span class="downloads-item__description"></span>
    <div class="downloads-item__button">
        <button class="button">
            <span class="button__text"></span>
        </button>
        ${Icon.rightArrow}
    </div>
</a>
  `;

  /**
   * Error state for the product details when the product is not available.
   */
  productErrorState = `
<div class="e-catalog__product-detail-error-state">
    <h4 class="e-catalog__product-detail-error-state-title"><span class="text-color-light"></span></h4>
    <div class="button-section button-section--small-margin">
      <button class="button">
        <span class="button__text"></span>
      </button>
    </div>
</div>`;

  /**
   * Setup the component with translations
   * @param translations The translations to initialize
   */
  constructor(translations: { [key: string]: string }) {
    super();
    this.translations = translations;
  }

  /**
   * Create the detail page view
   */
  renderView(): void {
    // if coming from the result list, the scroll position may not be at the top, reset this
    this.resetScrollInformation();

    // hide the mobile menu
    header.hideCategoryHeader();

    // hide the ventilator dropdown and the search bar (only on mobile)
    header.hideVentilatorsAndSearchOnMobile();

    // hide the sub nav
    header.showSubNav(false);

    // Hide the headers shadow before user scrolled
    header.setHideShadowBeforeScrolled(true);

    // set the correct back button title
    eCatalog.updateMobileMenuHeadingAndBackButton(document.querySelector('.section--header'), null, null, 'detail');

    this.renderTranslations();

    getProductDetail(readUrlParameter(Param.PRODUCT)).then((product) => {
      if (product) {
        const cartButton = this.pageComponent.querySelector<HTMLButtonElement>('#product-detail-cart-button');

        this.renderImages(product);
        this.renderProductTable(product);
        if (product.price !== undefined && product.price.length > 0 && product.isWebshopUser && product.catalog_taxonomies.indexOf('8b403b52-321c-4a5a-abbc-0dd4fab6877e') > -1) {
          this.addListeners(product, cartButton);
        } else {
          cartButton.parentElement.removeChild(cartButton);
        }

        const backButton = this.pageComponent.querySelector('#e-catalog-detail-back-button');
          backButton.addEventListener('click', () => {
            const selectedCategory = readUrlParameter(Param.CATEGORY);
            if (selectedCategory) {
              // return to the e-catalog page if there was a category selected before
              writeToUrl([], [Param.PRODUCT]);
              setTimeout(() => {
                if (window.localStorage && window.localStorage.getItem('scrollPosition')) {
                  console.log('Scrolling to ', window.localStorage.getItem('scrollPosition'));
                  window.scroll(0, parseInt(window.localStorage.getItem('scrollPosition')));
                  window.localStorage.removeItem('scrollPosition');
                }
              }, 50);
            } else {
              // otherwise, just go back to whichever page the user visited before.
              window.history.back();
            }
          });
          
        // set the page title
        document.title = product.name;

        const description = this.pageComponent.querySelector<HTMLElement>('.product-detail-description');
        if (product.description) {
          description.appendChild(createDivElementFromString(product.description));
        }

        if (product.deviceConfiguration?.length > 0) {
          this.renderDeviceConfiguration(product.deviceConfiguration);
        } else {
          // if the product details table is empty -> hide it on mobile
          description.classList.add('product-detail-description--single-column-mobile');
        }

        if (product.relatedDocuments?.length > 0) {
          this.renderDownloadTable(product.relatedDocuments, product.name);
        }

        // set the id for the pin button
        const pinButton = this.pageComponent.querySelector<HTMLButtonElement>('#product-detail-wishlist-button');
        pinButton.dataset.overlayAssociatedId = product['@id'];

        // retrieve the wish list so we can display which products are already in it (blue pin button)
        this.updatePinAndShoppingCartButtonState();

        // set info message if product is not public
        if (product.accessType === 'partnernet') {
          const socialShare = this.pageComponent.querySelector<HTMLElement>('.social-share');
          socialShare.classList.add('social-share--with-text');
          const info = createHtmlElementFromString(
            `<div class="social-share__info">${this.translations.accessInfo}</div>`
          );
          socialShare.insertBefore(info, socialShare.firstElementChild);
        }

        formatText(document.querySelector('.section--product-detail'));
      } else {
        this.renderProductErrorState();
      }
    });
  }

  /**
   * Resets the scroll information for the infinite loading on a new tab.
   * @param root The global search wrapper root element
   */
  resetScrollInformation(): void {
    // Scroll to top again
    document.documentElement.scrollTop = 0;
  }

  /**
   * Update the already visible pin buttons (because wish list items may have changed).
   */
  updatePinAndShoppingCartButtonState(): void {
    if (this.pageComponent) {
      const pinButton = this.pageComponent.querySelector<HTMLElement>('#product-detail-wishlist-button');
      const cartButton = this.pageComponent.querySelector<HTMLButtonElement>('#product-detail-cart-button');
      wishListData
        .getCachedWishListProductIds(false)
        .then((productsInWishlists) => {
          const productId = readUrlParameter(Param.PRODUCT);
          // make the pin button primary (blue) if the product is already part of a wish list
          pinButton.classList.toggle('button--primary', productsInWishlists.includes(productId));
        })
        .catch(() => {
          // no user data (logged out), remove the pin and shopping cart buttons
          pinButton?.parentElement.removeChild(pinButton);
          cartButton?.parentElement.removeChild(cartButton);
        });
    }
  }

  /**
   * Add click listeners for shopping cart and back button.
   * The wishlist button is handled via id.
   * @param product The selected product
   * @param cartButton The shopping cart button element
   */
  addListeners(product: HamiltonProduct, cartButton: HTMLButtonElement): void {
    cartButton.addEventListener('click', () =>
      addToShoppingCart(
        convertToReducedProduct(product),
        this.translations.addedToShoppingCart,
        this.translations.goToShoppingCart,
        this.translations.errorMessage
      )
    );
  }

  /**
   * Renders the translated text for the product details
   */
  renderTranslations(): void {
    this.pageComponent.querySelector<HTMLElement>('#e-catalog-detail-back-button .button__text').innerText =
      this.translations.backToProducts;
    this.pageComponent.querySelector<HTMLElement>('.social-share__text').innerText = this.translations.shareOn;
    this.pageComponent.querySelector<HTMLElement>('.social-share__copy').dataset.labelCopied =
      this.translations.copiedInfo;
  }

  /**
   * Renders the images of the product
   * @param product The product to get the images from
   */
  renderImages(product: HamiltonProductDetail): void {
    // Set up the main image
    const imageWrapper = this.pageComponent.querySelector<HTMLElement>('.text-and-image-component__image-wrapper');
    const image = `<img src="${getUrlForImage(product.image?.renditions?.large?.link)}" alt="${product.name}" />`;
    imageWrapper.appendChild(createHtmlElementFromString(image));

    imageWrapper.setAttribute('data-fallback-icons', product.icons ? JSON.stringify(product.icons) : '');

    const imageUrl = getUrlForImage(product.image?.renditions?.large?.link);
    const itemImage = imageWrapper.querySelector('img');
    if (imageUrl) {
      // if there is an image for the product, show it
      itemImage.src = imageUrl;
    } else {
      // otherwise, show the large version of the category icon for this product
      imageWrapper.removeChild(itemImage);
      const categoryIconLarge = getFallbackIconForCategory(product.icons, true);
      imageWrapper.appendChild(categoryIconLarge);
    }

    // Add product gallery images if available
    if (product.gallery?.length > 0) {
      // Add thumbnail from main image
      const imageThumbnails = this.pageComponent.querySelector<HTMLElement>('.text-and-image-component__thumbnails');
      const thumbnail = `<img src="${getUrlForImage(product.image?.renditions?.small?.link)}" alt="${product.name}" />`;
      imageThumbnails.appendChild(createHtmlElementFromString(thumbnail));
      // Add images and thumbnails for all gallery images
      product.gallery.forEach((image) => {
        const packingLabelClass = image.isPackingLabel ? 'packing-label' : '';
        const largeImage = `<img src="${getUrlForImage(image?.renditions?.large?.link)}" alt="${
          product.name
        }" class="${packingLabelClass}" />`;
        imageWrapper.appendChild(createHtmlElementFromString(largeImage));
        const thumbnail = `<img src="${getUrlForImage(image.renditions?.small?.link)}" alt="${product.name}" />`;
        imageThumbnails.appendChild(createHtmlElementFromString(thumbnail));
      });

      imageWrapper.parentElement.parentElement.classList.add('text-and-image-component--gallery');
      textAndImageComponent.initialize(imageWrapper.parentElement.parentElement);
    }
  }

  /**
   * Renders the product table depending on the available properties.
   * @param product The product to create the table from
   */
  renderProductTable(product: HamiltonProductDetail): void {
    this.pageComponent.querySelector<HTMLElement>('.product-detail__headline').innerText = product.name;
    this.pageComponent.querySelector<HTMLElement>('.product-detail__id').innerText = product.partNumber + ' ';
    const tag =
      product.newOrUpdated === 'new'
        ? `<span class="tag tag--small tag--green">${this.translations.ageFilterNewLabel}</span>`
        : product.newOrUpdated === 'updated'
        ? `&nbsp;<span class="tag tag--small">${this.translations.ageFilterUpdatedLabel}</span>`
        : '';
    if (tag !== '') {
      this.pageComponent
        .querySelector<HTMLElement>('.product-detail__id')
        .appendChild(createHtmlElementFromString(tag));
    }

    const table = this.pageComponent.querySelector<HTMLTableElement>('.product-detail-table');
    let cellIndex = 0;

    if (product.typeOfUse) {
      const icon = createUseIconsFromId(product.typeOfUse, this.translations);
      this.addTableRow(table, cellIndex, this.translations.typeOfUse, icon);
      cellIndex++;
    }
    if (product.patientGroups) {
      const icon = createPatientGroupIconsFromString(product.patientGroups, this.translations);
      this.addTableRow(table, cellIndex, this.translations.patientGroups, icon);
      cellIndex++;
    }
    const packingUnit = createHtmlElementFromString(
      `<div class="packing-unit" title="${this.translations.packingUnit}">${packagingUnitOrDefault(product)}</div>`
    );
    this.addTableRow(table, cellIndex, this.translations.packingUnit, packingUnit);
    cellIndex++;
    if (product.forVentilator) {
      this.addTableRow(table, cellIndex, this.translations.forVentilator, product.forVentilator);
      cellIndex++;
    }
    if (product.material) {
      this.addTableRow(table, cellIndex, this.translations.material, product.material);
      cellIndex++;
    }
    if (product.note) {
      const noteElement = document.createElement('div');
      noteElement.innerHTML = product.note.replaceAll('\n', '');
      this.addTableRow(table, cellIndex, this.translations.noteLabel, noteElement);
      cellIndex++;
    }
    if (product.gtin) {
      this.addTableRow(table, cellIndex, this.translations.gtin, product.gtin);
      cellIndex++;
    }
    if (product.height || product.length || product.width) {
      let sizes = [];
      let labels = [];
      let sizeValue = '';
      let sizeLabel = this.translations.dimensionsUnit;

      if (product.width) {
        sizes.push(product.width);

        if (this.translations.dimensionWidth) {
          labels.push(this.translations.dimensionWidth);
        }
      }
      if (product.length) {
        sizes.push(product.length);

        if (this.translations.dimensionLength) {
          labels.push(this.translations.dimensionLength);
        }
      }
      if (product.height) {
        sizes.push(product.height);

        if (this.translations.dimensionHeight) {
          labels.push(this.translations.dimensionHeight);
        }
      }

      sizeValue = sizes.join('x');
      if (sizeLabel.length > 0 && this.translations.dimensionUnitLabel) {
        sizeLabel = '(' + labels.join('x') + ' ' + (this.translations.dimensionUnitLabel || '') + ')';
        this.addTableRow(
          table,
          cellIndex,
          this.translations.dimensions,
          `${sizeValue} ${sizeLabel}`
        );
      } else {
        this.addTableRow(
          table,
          cellIndex,
          this.translations.dimensions,
          `${product.width||0}x${product.length||0}x${product.height||0} ${this.translations.dimensionsUnit}`
        );
      }
      
      cellIndex++;
    }
    if (product.weight) {
      this.addTableRow(table, cellIndex, this.translations.weight, `${product.weight} kg`);
      cellIndex++;
    }
    if (product.packagingHeight && product.packagingLength && product.packagingWidth) {
      this.addTableRow(
        table,
        cellIndex,
        this.translations.packagingDimensions,
        `${product.packagingWidth}x${product.packagingLength}x${product.packagingHeight} ${this.translations.packagingDimensionsUnit}`
      );
      cellIndex++;
    }
    if (product.packagingWeight) {
      this.addTableRow(table, cellIndex, this.translations.packagingWeight, `${product.packagingWeight} kg`);
      cellIndex++;
    }
    if (product.shelfLife) {
      this.addTableRow(table, cellIndex, this.translations.shelfLife, `${product.shelfLife}`);
      cellIndex++;
    }

    if (product.includedArticles) {
      const includedArticlesElem = document.createElement('span');
      for (const article of product.includedArticles) {
        const articleLink = document.createElement('a');
        articleLink.innerText = `${this.translations.pn} ${article.partNumber} `;
        articleLink.id = 'product_' + article['@id'];
        includedArticlesElem.appendChild(articleLink);
        includedArticlesElem.appendChild(document.createTextNode(article.name));
        includedArticlesElem.appendChild(document.createElement('br'));
      }

      this.addTableRow(table, cellIndex, this.translations.includedArticles, includedArticlesElem);
      cellIndex++;

      setTimeout(() => {
        for (const article of product.includedArticles) {
          document.querySelector('#product_' + article['@id']).addEventListener('click', () => {
            writeToUrl({ param: Param.PRODUCT, value: article['@id'] }, [Param.TAB, Param.CATEGORY]);
          });
        }
      }, 10);
    }

    if (product.requiredArticles) {
      const requiredArticlesElem = document.createElement('span');
      for (const article of product.requiredArticles) {
        const articleLink = document.createElement('a');
        articleLink.innerText = `${this.translations.pn} ${article.partNumber} `;
        articleLink.id = 'product_' + article['@id'];
        requiredArticlesElem.appendChild(articleLink);
        requiredArticlesElem.appendChild(document.createTextNode(article.name));
        requiredArticlesElem.appendChild(document.createElement('br'));
      }

      this.addTableRow(table, cellIndex, this.translations.requiredArticles, requiredArticlesElem);
      cellIndex++;

      setTimeout(() => {
        for (const article of product.includedArticles) {
          document.querySelector('#product_' + article['@id']).addEventListener('click', () => {
            writeToUrl({ param: Param.PRODUCT, value: article['@id'] }, [Param.TAB, Param.CATEGORY]);
          });
        }
      }, 10);
    }

    if (product.price) {
      this.addTableRow(
        table,
        cellIndex,
        this.translations.priceExclVat,
        getPriceWithCurrencyForOnePiece(product.price)
      );
    }
  }

  /**
   * Adds a row to the product detail table.
   * @param table The table to add a row to
   * @param index The current index
   * @param label The label for the th element
   * @param value The value for the td element
   */
  addTableRow(table: HTMLTableElement, index: number, label: string, value: HTMLElement | string): void {
    const tableRow = table.insertRow(index);
    tableRow.innerHTML = this.productDetailsRow;
    tableRow.querySelector('th').innerText = label;
    if (value instanceof HTMLElement) {
      tableRow.querySelector('td').appendChild(value);
    } else {
      tableRow.querySelector('td').innerText = value;
    }
  }

  /**
   * Renders the device configuration with headline and table.
   * @param configuration The configuration to render
   */
  renderDeviceConfiguration(configuration: HamiltonDeviceConfiguration[]): void {
    const table = this.pageComponent.querySelector<HTMLElement>('.product-detail__table');
    const headline = `<h4 class="table__headline">${this.translations.correspondingPartNumber}</h4>`;
    table.appendChild(createHtmlElementFromString(headline));
    configuration.forEach((device) => {
      const row = createHtmlElementFromString(`<div class="table__row">
          <div>${device.partNumber}</div>
          <div>${device.name}</div>
          <div>${this.translations.gtin}: ${device.gtin}</div>
        </div>`);
      table.appendChild(row);
    });
  }

  /**
   * Renders the download table from the related documents.
   * @param downloads The related documents from the product
   * @param name The name of the product
   */
  renderDownloadTable(downloads: HamiltonDocument[], name: string): void {
    // Append table to the product detail markup
    this.pageComponent.appendChild(createHtmlElementFromString(this.downloadsTable));
    const table = this.pageComponent.querySelector<HTMLElement>('.downloads-table');
    // Set headline
    table.firstElementChild.innerHTML = `${this.translations.documentsFor} ${name}`;
    // Create download item
    downloads.forEach((download) => {
      const item = createHtmlElementFromString(this.downloadItem) as HTMLAnchorElement;
      item.href = baseUrl + download.renditions.link;
      item.querySelector<HTMLElement>('.downloads-item__title').innerText =
        download.renditions.title || download.metadata.fileName; // Use file name as fallback if title is empty
      table.appendChild(item);
      const isVideo = download.metadata.mimeType.indexOf('video') !== -1;
      const isPdf = download.metadata.mimeType.indexOf('pdf') !== -1;
      item.querySelector<HTMLElement>('.downloads-item__type').innerText =
        !isVideo && !isPdf ? '' : this.translations[`${isVideo ? 'fileVideo' : 'filePdf'}`];
      item.querySelector('.downloads-item__icon').innerHTML = isVideo ? Icon.video : isPdf ? Icon.pdf : Icon.link;
      item.querySelector<HTMLElement>('.button__text').innerText =
        this.translations[`${isVideo ? 'watchVideo' : 'download'}`];

      const id = this.joinValues('.', download.renditions.hmIdentifier, download.renditions.revisionNumber);
      item.querySelector<HTMLElement>('.downloads-item__description').innerText = this.joinValues(
        ' | ',
        download.renditions.languages,
        convertFileSize(download.metadata.fileSize),
        id
      );
    });
  }

  /**
   * Render the error state, when the product is not available.
   */
  renderProductErrorState(): void {
    this.removeProductDetails();
    const errorStateElement = createHtmlElementFromString(this.productErrorState);
    const subTitle = errorStateElement.querySelector<HTMLElement>('.e-catalog__product-detail-error-state-title span');
    const title = errorStateElement.querySelector<HTMLElement>('.e-catalog__product-detail-error-state-title');
    const backToECatalogButton = errorStateElement.querySelector<HTMLElement>('.button');
    const backButtonLabel = backToECatalogButton.querySelector<HTMLElement>('span');

    // Insert translations
    const titleTextNode = document.createTextNode(this.translations.productNotAvailableSorry + ' ');
    title.insertBefore(titleTextNode, subTitle);
    subTitle.innerText = this.translations.productNotAvailable;
    backButtonLabel.innerText = this.translations.productNotAvailableBackButtonTitle;

    // listen for clicks on the back button
    backToECatalogButton.addEventListener(
      'click',
      () => {
        writeToUrl([], [Param.PRODUCT, Param.CATEGORY, Param.TAB]);
        setTimeout(() => {
          if (window.localStorage && window.localStorage.getItem('scrollPosition')) {
            console.log('Scrolling to ', window.localStorage.getItem('scrollPosition'));
            window.scroll(0, parseInt(window.localStorage.getItem('scrollPosition')));
            window.localStorage.removeItem('scrollPosition');
          }
        }, 50);
      },
      false
    );

    // and finally append the error state to the details page
    const detailsSection = document.querySelector('.section--product-detail');
    detailsSection.appendChild(errorStateElement);
  }

  /**
   * Removes the empty state.
   */
  removeProductDetails(): void {
    const detailsSection = document.querySelector('.section--product-detail');
    detailsSection.innerHTML = '';
  }

  /**
   * Joins the given string values if it is not empty and returns a joined string by the given delimiter.
   * @param delimiter The delimiter to join the values
   * @param values The values to join
   */
  joinValues(delimiter: string, ...values: string[]): string {
    const description = [];
    values.forEach((value) => {
      if (value.length > 0) {
        description.push(value);
      }
    });
    return description.join(delimiter);
  }
}
